import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Sidebar } from "../ui/sidebar";
import { cn } from "../../lib/utils";
import logger from "../../utils/logger";
import * as localService from "../../services/localService";
import apiService from "../../services/apiService";
import { useSubscription } from "../../hooks/useSubscription";
import {
  logoutUser,
  resetTotalAmount,
  saveCartDetails,
  setCartEntries,
  clearCountries,
} from "../../redux/actions/dashboard";
import { SidebarProvider, useSidebar } from "../../contexts/SidebarContext";
import { Constants } from "../../utils/constants";
import LogoutModal from "../LogoutModal";
import { Popover, PopoverContent, PopoverTrigger } from "../../shadcn/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../shadcn/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../shadcn/ui/tabs";
import { Badge } from "../../shadcn/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../shadcn/ui/avatar";
import { Button } from "../../shadcn/ui/button";
import AccountSwitcher from "../AccountSwitcher";
import useNotificationWebSocket from "../../hooks/useNotificationWebSocket";
import EmailVerificationBanner from "../EmailVerificationBanner";

// Notification Bell Icon
const NotificationIcon = ({ hasNotifications }) => (
  <div className="relative">
    <svg className="w-10 h-10 text-gray-600 hover:text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
    {hasNotifications && (
      <span className="absolute top-[-2px] right-[-2px] block h-2 w-2 rounded-full bg-blue-500"></span>
    )}
  </div>
);

// Subscription Details Dropdown
const SubscriptionDetails = ({ isOpen, onClose, subscriptionData }) => {
  if (!isOpen) return null;

  const { planName, meetingRoomCheckins, meetingRoomTime, coworkingCheckins, coworkingTime, expiryDate } =
    subscriptionData;

  return (
    <div className="absolute left-0 top-full z-20 mt-1 w-80 bg-white rounded-lg border border-gray-200 shadow-lg">
      <div className="p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Subscription Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Plan:</span>
            <span className="text-sm font-medium">{planName}</span>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <p className="mb-1 text-xs font-medium text-gray-500">Meeting Room</p>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Check-ins:</span>
              <span className="text-sm font-medium">{meetingRoomCheckins}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Time:</span>
              <span className="text-sm font-medium">{meetingRoomTime}</span>
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <p className="mb-1 text-xs font-medium text-gray-500">Co-working</p>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Check-ins:</span>
              <span className="text-sm font-medium">{coworkingCheckins}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Time:</span>
              <span className="text-sm font-medium">{coworkingTime}</span>
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Expiry Date:</span>
              <span className="text-sm font-medium">{expiryDate}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Format the notification time (e.g., "2 hours ago")
const formatNotificationTime = date => {
  if (!date) return "";

  // Ensure we're working with a Date object
  const notificationDate = date instanceof Date ? date : new Date(date);

  const now = new Date();
  const diffMs = now - notificationDate;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffDay > 30) {
    // For older notifications, show the actual date
    return notificationDate.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } else if (diffDay > 0) {
    return diffDay === 1 ? "1 day ago" : `${diffDay} days ago`;
  } else if (diffHour > 0) {
    return diffHour === 1 ? "1 hour ago" : `${diffHour} hours ago`;
  } else if (diffMin > 0) {
    return diffMin === 1 ? "1 minute ago" : `${diffMin} minutes ago`;
  } else {
    return "Just now";
  }
};

// Notifications Popover using Shadcn
const NotificationsPopover = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  isLoading,
  onLoadMore,
  hasMore,
  isLoadingMore,
}) => {
  const [activeTab, setActiveTab] = useState("all"); // "all", "unread", or "read"
  const scrollContainerRef = useRef(null);

  // Calculate counts for each category
  const allCount = notifications.length;
  const unreadCount = notifications.filter(n => !n.read).length;
  const readCount = notifications.filter(n => n.read).length;

  // Filter notifications based on active tab
  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === "all") return true;
    if (activeTab === "unread") return !notification.read;
    if (activeTab === "read") return notification.read;
    return true;
  });

  // Handle infinite scroll - only load more when viewing "all" tab
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || activeTab !== "all" || !hasMore || isLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Trigger load more when scrolled within 50px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 50) {
      onLoadMore?.();
    }
  }, [activeTab, hasMore, isLoadingMore, onLoadMore]);

  return (
    <PopoverContent className="w-80 p-0" align="end">
      <div className="p-3 border-b">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
        </div>
      </div>

      {/* Filter tabs using Shadcn Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 rounded-none border-b bg-white">
          <TabsTrigger
            value="all"
            className="flex items-center gap-2 bg-white data-[state=active]:bg-white data-[state=active]:text-blue-600"
          >
            All
            <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-white text-gray-700 border border-gray-300">
              {allCount}
            </Badge>
          </TabsTrigger>
          <TabsTrigger
            value="unread"
            className="flex items-center gap-2 bg-white data-[state=active]:bg-white data-[state=active]:text-blue-600"
          >
            Unread
            <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-white text-gray-700 border border-gray-300">
              {unreadCount}
            </Badge>
          </TabsTrigger>
          <TabsTrigger
            value="read"
            className="flex items-center gap-2 bg-white data-[state=active]:bg-white data-[state=active]:text-blue-600"
          >
            Read
            <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-white text-gray-700 border border-gray-300">
              {readCount}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="m-0">
          <div ref={scrollContainerRef} className="overflow-y-auto max-h-72" onScroll={handleScroll}>
            {isLoading ? (
              <div className="p-4 text-center">
                <svg
                  className="mx-auto w-5 h-5 text-blue-600 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <p className="mt-2 text-sm text-gray-500">Loading notifications...</p>
              </div>
            ) : filteredNotifications.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {filteredNotifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`p-3 hover:bg-gray-50 cursor-pointer ${!notification.read ? "bg-blue-50" : ""}`}
                    onClick={() => {
                      if (!notification.read) {
                        onMarkAsRead(notification.id);
                      }
                    }}
                  >
                    <div className="flex items-start">
                      <div
                        className={`flex-shrink-0 h-2.5 w-2.5 mt-1.5 rounded-full ${!notification.read ? "bg-blue-600" : "bg-transparent"}`}
                      ></div>
                      <div className="flex-1 ml-2">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                          {notification.priority && notification.priority !== "LOW" && (
                            <Badge
                              variant="secondary"
                              className={`text-xs px-2 py-0.5 ${
                                notification.priority === "HIGH"
                                  ? "bg-red-100 text-red-700"
                                  : notification.priority === "URGENT"
                                    ? "bg-orange-100 text-orange-700"
                                    : "bg-yellow-100 text-yellow-700"
                              }`}
                            >
                              {notification.priority}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{notification.message}</p>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-gray-400">{formatNotificationTime(notification.createdAt)}</p>
                          {notification.cta && notification.data?.accept && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-xs text-blue-600 border-blue-300 hover:bg-blue-50"
                              onClick={e => {
                                e.stopPropagation();
                                if (!notification.read) {
                                  onMarkAsRead(notification.id);
                                }
                                window.open(notification.data.accept, "_blank");
                              }}
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="p-3 text-center">
                    <svg
                      className="mx-auto w-4 h-4 text-blue-600 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 text-center">
                <p className="text-sm text-gray-500">
                  {activeTab === "all"
                    ? "No notifications yet"
                    : activeTab === "unread"
                      ? "No unread notifications"
                      : "No read notifications"}
                </p>
              </div>
            )}
          </div>

          {/* Only show mark all as read button if there are unread notifications */}
          {unreadCount > 0 && (
            <div className="p-2 border-t border-gray-100">
              <Button
                onClick={onMarkAllAsRead}
                variant="ghost"
                size="sm"
                className="w-full text-xs text-blue-600 hover:text-blue-800"
              >
                Mark all as read
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PopoverContent>
  );
};

// Profile Dropdown using Shadcn
const ProfileDropdown = ({ currentUser, onLogout }) => {
  const history = useHistory();
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [savedAccounts, setSavedAccounts] = useState([]);

  // Load saved accounts on mount
  useEffect(() => {
    const loadAccounts = async () => {
      const { getSavedAccounts } = await import("../../services/credentialService");
      const accounts = getSavedAccounts();
      setSavedAccounts(accounts);
    };
    loadAccounts();
  }, []);

  // Get user role from organizationRole or fallback to role
  const userRole = currentUser.organizationRole?.code || currentUser.role || "Unknown Role";

  // Format the role for display: remove "ROLE_" prefix, replace underscores with spaces, capitalize first letter
  const formatRoleForDisplay = role => {
    if (!role) return "Unknown Role";

    // Remove "ROLE_" prefix if it exists
    let formattedRole = role.replace(/^ROLE_/, "");

    // Replace underscores with spaces
    formattedRole = formattedRole.replace(/_/g, " ");

    // Capitalize first letter of each word
    formattedRole = formattedRole
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    return formattedRole;
  };

  const displayRole = formatRoleForDisplay(userRole);

  return (
    <DropdownMenuContent className="w-56" align="end">
      <div className="p-3 border-b">
        <div className="flex items-center">
          <Avatar className="h-10 w-10">
            {currentUser?.imageUrl && <AvatarImage src={currentUser.imageUrl} alt="Profile" />}
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {currentUser.firstName?.[0]}
              {currentUser.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{`${currentUser.firstName} ${currentUser.lastName}`}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <p className="text-xs text-gray-500 truncate">{currentUser.emailAddress || currentUser.email}</p>
              {currentUser?.emailConfirmed ? (
                <div
                  className="flex-shrink-0 w-3 h-3 rounded-full bg-green-500 flex items-center justify-center"
                  title="Email Verified"
                >
                  <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 8 8">
                    <path
                      d="M6.41 0l-.69.72-2.78 2.78-.81-.78-.72-.72-1.41 1.41.72.72 1.5 1.5.69.72.72-.72 3.5-3.5.72-.72-1.44-1.41z"
                      transform="translate(0 1)"
                    />
                  </svg>
                </div>
              ) : (
                <div
                  className="flex-shrink-0 w-3 h-3 rounded-full bg-amber-500 flex items-center justify-center"
                  title="Email Unverified"
                >
                  <span className="text-[8px] font-bold text-white">!</span>
                </div>
              )}
            </div>
            <p className="text-xs text-blue-600 font-medium mt-0.5">{displayRole}</p>
          </div>
        </div>
      </div>

      <DropdownMenuItem onClick={() => history.push("/settings")} className="cursor-pointer hover:bg-gray-50">
        <div className="flex items-center space-x-3">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>Settings</span>
        </div>
      </DropdownMenuItem>

      {/* Switch Account Menu Item */}
      {savedAccounts.length > 0 && (
        <DropdownMenuItem
          onClick={() => setShowAccountSwitcher(!showAccountSwitcher)}
          className="cursor-pointer hover:bg-gray-50"
        >
          <div className="flex items-center space-x-3">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            <span>Switch Account</span>
            {savedAccounts.length > 1 && (
              <Badge variant="secondary" className="ml-auto text-xs">
                {savedAccounts.length - 1}
              </Badge>
            )}
          </div>
        </DropdownMenuItem>
      )}

      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={onLogout}
        className="cursor-pointer text-red-600 hover:bg-red-50 focus:text-red-600 focus:bg-red-50"
      >
        <div className="flex items-center space-x-3">
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
          <span>Sign out</span>
        </div>
      </DropdownMenuItem>

      {/* Account Switcher Section */}
      {showAccountSwitcher && savedAccounts.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <div className="px-2 py-2">
            <AccountSwitcher
              accounts={savedAccounts.filter(
                acc => acc.username !== (currentUser.username || currentUser.emailAddress)
              )}
              currentUser={currentUser}
              variant="dropdown"
              onSelect={async account => {
                try {
                  // Import services dynamically
                  const { getSingleCredential } = await import("../../services/credentialService");
                  const { apiV2Service } = await import("../../services/apiV2Service");

                  // Get credentials
                  const cred = await getSingleCredential("required");

                  if (cred && cred.username === account.username && cred.password) {
                    // Logout current user
                    await onLogout();

                    // Auto-login with selected account
                    try {
                      const res = await apiV2Service.loginV2(cred.username, cred.password);
                      const { handlePostAuthFlow: authFlow } = await import("../../shared/auth");
                      await authFlow({
                        user: res.user,
                        access_token: res.access_token,
                        refresh_token: res.refresh_token,
                      });
                    } catch (error) {
                      logger.error("Auto-login failed:", error);
                      // Redirect to login page with pre-filled username
                      history.push(`/signin?username=${encodeURIComponent(account.username)}`);
                    }
                  } else {
                    // Redirect to login with pre-filled username
                    await onLogout();
                    history.push(`/signin?username=${encodeURIComponent(account.username)}`);
                  }
                } catch (error) {
                  logger.error("Error switching account:", error);
                  // Fallback: redirect to login
                  await onLogout();
                  history.push("/signin");
                }
              }}
              onRemove={async username => {
                const { removeAccount, getSavedAccounts } = await import("../../services/credentialService");
                removeAccount(username);
                setSavedAccounts(getSavedAccounts());
              }}
            />
          </div>
        </>
      )}
    </DropdownMenuContent>
  );
};

export function AuthenticatedLayout({ children, className, ...props }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [navItems, setNavItems] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    activeSubscription,
    loading: subscriptionLoading,
    subscriptionWallChecked,
    hasActiveSubscription: subHookHasActive,
  } = useSubscription();
  const isDebugSkipSub =
    typeof window !== "undefined" &&
    window.location.hostname === "localhost" &&
    localStorage.getItem("debug_skip_subscription") === "true";
  const hasActiveSubscription = !!activeSubscription || subHookHasActive || isDebugSkipSub;
  const userRoleCode = currentUser?.organizationRole?.code || currentUser?.role || "";
  const orgTypeCode = currentUser?.organization?.type?.code || "";
  const requiresSubscription =
    (orgTypeCode === "HOST_PROPERTY_MANAGEMENT" || orgTypeCode === "CORPORATE_CUSTOMER") &&
    userRoleCode !== "ROLE_HOST_CUSTOMER_INDIVIDUAL";
  const shouldShowSidebar = !requiresSubscription || hasActiveSubscription;

  // Fetch subscriptions on mount if required and not already checked
  useEffect(() => {
    if (requiresSubscription && !subscriptionWallChecked && currentUser?.organizationId) {
      const fetchSubs = async () => {
        try {
          const { checkSubscriptionWall } = await import("../../redux/actions/subscription");
          await dispatch(checkSubscriptionWall(currentUser));
        } catch (error) {
          logger.error("Error checking subscription wall:", error);
        }
      };
      fetchSubs();
    }
  }, [requiresSubscription, subscriptionWallChecked, currentUser, dispatch]);

  // Redirect to pricing if subscription is required but not active
  useEffect(() => {
    // Don't redirect while subscriptions are still loading or not yet checked
    if (subscriptionLoading || !subscriptionWallChecked) {
      return;
    }

    if (requiresSubscription && !hasActiveSubscription) {
      const pricingPath = orgTypeCode === "CORPORATE_CUSTOMER" ? "/subscriber/pricing" : "/host/pricing";
      const currentPath = history.location.pathname;

      // Only redirect if not already on pricing page
      if (currentPath !== pricingPath) {
        history.replace(pricingPath);
      }
    }
  }, [requiresSubscription, hasActiveSubscription, orgTypeCode, history, subscriptionLoading, subscriptionWallChecked]);

  // Block navigation when subscription is required but not active
  useEffect(() => {
    // Don't block while subscriptions are still loading or not yet checked
    if (subscriptionLoading || !subscriptionWallChecked) return;

    if (!requiresSubscription || hasActiveSubscription) return;

    const pricingPath = orgTypeCode === "CORPORATE_CUSTOMER" ? "/subscriber/pricing" : "/host/pricing";

    const unblock = history.block(location => {
      // Allow navigation to pricing page only
      if (location.pathname === pricingPath) {
        return true;
      }
      // Block all other navigation
      return false;
    });

    return () => unblock();
  }, [requiresSubscription, hasActiveSubscription, orgTypeCode, history, subscriptionLoading, subscriptionWallChecked]);

  const getIconForMenu = useCallback(menuName => {
    const iconMap = {
      Dashboard: <Constants.DashboardIcon />,
      Properties: <Constants.PropertiesIcon />,
      "Spaces & Services": <Constants.SpacesAndServicesIcon />,
      "User Management": <Constants.UsersIcon />,
      Users: <Constants.UsersIcon />,
      Hosts: <Constants.HostsIcon />,
      Orders: <Constants.OrdersIcon />,
      Packages: <Constants.PackageIcon />,
      Announcements: <Constants.AnnouncementIcon />,
      Reservations: <Constants.BookingIcon />,
      "Find a Space": <Constants.SearchIcon />,
      Settings: <Constants.SettingsIcon />,
      Billing: <Constants.BillingIcon />,
      "API Explorer": <Constants.ApiExplorerIcon />,
      "Membership Plan": <Constants.MembershipPlanIcon />,
      "Membership plan": <Constants.MembershipPlanIcon />,
      "Roles & Permissions": <Constants.RolesPermissionsIcon />,
      "Service Categories": <Constants.ServiceCategoriesIcon />,
      "Offers & Discounts": <Constants.OffersDiscountsIcon />,
      "Payment Configuration": <Constants.PaymentConfigIcon />,
      "Policy Management": <Constants.PolicyManagementIcon />,
    };
    const icon = iconMap[menuName];
    if (!icon) {
      logger.warn(`No icon found for menu: ${menuName}`);
    }
    return icon || null;
  }, []);

  const convertMenuFormat = useCallback(
    menus => {
      return menus.map(menu => {
        const convertedMenu = {
          href: menu.route,
          label: menu.name,
          icon: getIconForMenu(menu.name),
          activeRoutes: menu.activeRoutes || [],
        };

        if (menu.children) {
          convertedMenu.children = menu.children.map(child => ({
            href: child.route,
            label: child.name,
            activeRoutes: child.activeRoutes || [],
          }));
        }

        return convertedMenu;
      });
    },
    [getIconForMenu]
  );

  useEffect(() => {
    const user = localService.getUser();
    if (!user) {
      history.push("/signin");
      return;
    }

    setCurrentUser(user);

    // Get navigation items based on user role and subscription status
    const userRole = user.organizationRole?.code || user.role;

    logger.log("🔍 AuthenticatedLayout - User role:", userRole, "Has active subscription:", hasActiveSubscription);

    const sidebarMenus = Constants.getMenusByRole(userRole, hasActiveSubscription);

    const availableNavItems = convertMenuFormat(sidebarMenus);

    setNavItems(availableNavItems);
  }, [history, hasActiveSubscription, convertMenuFormat]);

  // Re-run effect when subscription status might change
  useEffect(() => {
    if (!currentUser) return;

    const userRole = currentUser.organizationRole?.code || currentUser.role;

    const sidebarMenus = Constants.getMenusByRole(userRole, hasActiveSubscription);

    const availableNavItems = convertMenuFormat(sidebarMenus);
    setNavItems(availableNavItems);
  }, [currentUser, hasActiveSubscription, convertMenuFormat]);

  // Listen for profile updates (e.g., avatar) and refresh current user
  useEffect(() => {
    const handleUserUpdated = event => {
      const updatedUser = event?.detail || localService.getUser();
      if (updatedUser) {
        setCurrentUser(updatedUser);
      }
    };

    const handleStorage = event => {
      if (event.key === "currentUser") {
        const updated = localService.getUser();
        if (updated) {
          setCurrentUser(updated);
        }
      }
    };

    window.addEventListener("currentUserUpdated", handleUserUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("currentUserUpdated", handleUserUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // Auto-hide header on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const mainContent = document.getElementById("main-content-area");
      if (!mainContent) return;

      const currentScrollY = mainContent.scrollTop;

      // At the top of the page - always show header
      if (currentScrollY <= 10) {
        setIsHeaderVisible(true);
        setLastScrollY(currentScrollY);
        return;
      }

      // Scrolling down - hide header
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      }
      // Scrolling up - show header
      else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    // Add a small delay to ensure the DOM element exists
    const timeoutId = setTimeout(() => {
      const mainContent = document.getElementById("main-content-area");
      if (mainContent) {
        mainContent.addEventListener("scroll", handleScroll, { passive: true });
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      const mainContent = document.getElementById("main-content-area");
      if (mainContent) {
        mainContent.removeEventListener("scroll", handleScroll);
      }
    };
  }, [lastScrollY]);

  const initiateLogout = () => {
    setShowLogoutModal(true);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);

      // Disconnect WebSockets on logout
      const websocketService = await import("../../services/websocketService");
      const notificationWebSocketService = await import("../../services/notificationWebSocketService");
      websocketService.default.disconnect();
      notificationWebSocketService.default.disconnect();

      // Clear all user data from localStorage
      localService.clearAllUserData();

      // Reset Redux state
      dispatch(logoutUser());
      dispatch(saveCartDetails([]));
      dispatch(setCartEntries());
      dispatch(resetTotalAmount());
      dispatch(clearCountries());

      // Simulate API call for logout
      await new Promise(resolve => setTimeout(resolve, 500));

      // Redirect to signin page
      history.push("/signin");
    } catch (error) {
      logger.error("Logout failed:", error);
      setIsLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  if (!currentUser) {
    return null; // Or a loading state
  }

  return (
    <SidebarProvider>
      <div className="flex overflow-hidden h-screen bg-background">
        {shouldShowSidebar && <Sidebar items={navItems} user={currentUser} onLogout={initiateLogout} />}

        <div className="flex flex-col flex-1 h-full overflow-hidden">
          {/* Header wrapper - controls visibility */}
          <div
            className={`flex-shrink-0 transition-all duration-300 ease-in-out ${
              isHeaderVisible ? "h-16 opacity-100" : "h-0 opacity-0"
            }`}
          >
            <HeaderContent currentUser={currentUser} onLogout={initiateLogout} showSidebar={shouldShowSidebar} />
          </div>

          <EmailVerificationBanner user={currentUser} />

          {/* Main content - SCROLLABLE with calculated height */}
          <main
            id="main-content-area"
            className={cn("overflow-y-auto p-0", className)}
            style={{ height: isHeaderVisible ? "calc(100vh - 4rem)" : "100vh" }}
            {...props}
          >
            {children}
          </main>
        </div>

        {/* Logout confirmation modal */}
        <LogoutModal
          show={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleLogout}
          isLoading={isLoggingOut}
        />
      </div>
    </SidebarProvider>
  );
}

// Separate component to use sidebar context
function HeaderContent({ currentUser, onLogout, showSidebar = true }) {
  const { toggleMobileSidebar, mobileOpen } = useSidebar();
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  // Derive unread count from notifications array to keep in sync
  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  const subscriptionDetailsRef = useRef(null);

  // Helper to parse date from various formats (Java date object, ISO string, or timestamp)
  const parseNotificationDate = useCallback(dateValue => {
    if (!dateValue) return new Date();

    // Handle Java date object format
    if (dateValue.year !== undefined && dateValue.monthValue !== undefined) {
      return new Date(
        dateValue.year,
        dateValue.monthValue - 1, // JS months are 0-indexed
        dateValue.dayOfMonth || 1,
        dateValue.hour || 0,
        dateValue.minute || 0,
        dateValue.second || 0
      );
    }

    // Handle ISO string or timestamp
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }, []);

  // Helper to format a notification from WebSocket or REST API payload
  const formatNotification = useCallback(
    notification => ({
      id: notification.id || Date.now(),
      title: notification.title || "Notification",
      message: notification.message || notification.body || notification.content || "",
      createdAt: parseNotificationDate(notification.createdAt),
      read: notification.readStatus || false,
      priority: notification.priority || "LOW",
      roles: notification.roles || [],
      imageUrls: notification.imageUrls || [],
      cta: notification.cta || false,
      data: notification.data || null,
    }),
    [parseNotificationDate]
  );

  // WebSocket for real-time notifications with pagination
  const { loadMore, hasMore, isLoadingMore } = useNotificationWebSocket(
    // Callback for individual real-time notifications
    useCallback(
      notification => {
        logger.log("🔔 Real-time notification received:", notification);

        const notificationId = notification.id;

        // Add new notification to the list (with deduplication)
        setNotifications(prev => {
          // Check if notification already exists (by id)
          if (notificationId && prev.some(n => n.id === notificationId)) {
            logger.log("🔔 Notification already exists, skipping:", notificationId);
            return prev;
          }

          const newNotification = formatNotification(notification);
          return [newNotification, ...prev];
        });
      },
      [formatNotification]
    ),
    // Callback for batch notifications (on connect/reconnect or load more)
    useCallback(
      ({ notifications: newNotifications, pagination, isLoadMore }) => {
        logger.log(
          `📬 Received ${newNotifications.length} notifications, page ${pagination.page}, isLoadMore: ${isLoadMore}`
        );

        if (!isLoadMore) {
          // Initial load - replace notifications
          setIsLoadingNotifications(true);
          const formattedNotifications = newNotifications.map(formatNotification);
          formattedNotifications.sort((a, b) => b.createdAt - a.createdAt);
          logger.log("📬 Formatted notifications:", formattedNotifications);
          setNotifications(formattedNotifications);
          setIsLoadingNotifications(false);
        } else {
          // Load more - append notifications
          const formattedNotifications = newNotifications.map(formatNotification);
          setNotifications(prev => {
            // Deduplicate before appending
            const existingIds = new Set(prev.map(n => n.id));
            const newUniqueNotifications = formattedNotifications.filter(n => !existingIds.has(n.id));
            const combined = [...prev, ...newUniqueNotifications];
            // Sort by creation date (newest first)
            combined.sort((a, b) => b.createdAt - a.createdAt);
            return combined;
          });
        }
      },
      [formatNotification]
    )
  );

  // Mark a notification as read
  const markNotificationAsRead = async notificationId => {
    if (!currentUser?.id) return;

    try {
      await apiService.markNotificationAsRead(notificationId, currentUser.id);

      // Update notification in state (unreadCount is derived automatically)
      setNotifications(prev =>
        prev.map(notification => (notification.id === notificationId ? { ...notification, read: true } : notification))
      );
    } catch (error) {
      logger.error("Error marking notification as read:", error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsAsRead = async () => {
    if (!currentUser?.id || notifications.length === 0) return;

    // Get unread notifications before optimistic update (needed for revert on error)
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length === 0) return;

    try {
      // Update UI immediately for responsiveness (unreadCount is derived automatically)
      setNotifications(prev => prev.map(notification => ({ ...notification, read: true })));

      // Use the batch API endpoint to mark all as read in a single call
      await apiService.markAllNotificationsAsRead(currentUser.id);
    } catch (error) {
      logger.error("Error marking all notifications as read:", error);
      // Revert optimistic update on error - restore original read states
      setNotifications(prev =>
        prev.map(n => {
          const original = unreadNotifications.find(un => un.id === n.id);
          return original ? { ...n, read: false } : n;
        })
      );
    }
  };

  // Subscription data
  const subscriptionData = {
    planName: "Enterprise",
    meetingRoomCheckins: "10/30 used",
    meetingRoomTime: "5/20 hours used",
    coworkingCheckins: "8/25 used",
    coworkingTime: "12/40 hours used",
    expiryDate: "May 11, 2025",
  };

  // Handle clicks outside the subscription details dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (subscriptionDetailsRef.current && !subscriptionDetailsRef.current.contains(event.target)) {
        setShowSubscriptionDetails(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggleSidebar = e => {
    // Stop ALL types of event propagation to prevent issues with outside click handlers
    if (e) {
      e.preventDefault();
      e.stopPropagation();

      // Extra precautions to stop bubbling
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
        e.nativeEvent.preventDefault();
      }
    }

    // Add a small delay to ensure no conflicts with other event handlers
    setTimeout(() => {
      toggleMobileSidebar();
    }, 0);
  };

  return (
    <header className="flex flex-shrink-0 justify-between items-center px-6 h-16 bg-white border-b border-border">
      <div className="flex items-center">
        {/* Mobile menu button - only visible on mobile */}
        {showSidebar && (
          <button
            onClick={handleToggleSidebar}
            className="p-2 rounded-md md:hidden text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            aria-expanded={mobileOpen}
            aria-controls="mobile-sidebar"
            aria-label="Toggle mobile sidebar"
            data-sidebar-trigger
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* Subscription details - Desktop - Only show for individual subscribers */}
        {currentUser?.role === "ROLE_SUBSCRIBER_INDIVIDUAL" && (
          <div className="hidden relative ml-4 md:flex" ref={subscriptionDetailsRef}>
            <button
              onClick={() => setShowSubscriptionDetails(!showSubscriptionDetails)}
              className="flex items-center text-sm text-gray-700 hover:text-gray-900"
            >
              <span>Number of Check-ins Available: </span>
              <span className="ml-1 text-blue-600 underline hover:text-blue-800">See details</span>
            </button>
            <SubscriptionDetails
              isOpen={showSubscriptionDetails}
              onClose={() => setShowSubscriptionDetails(false)}
              subscriptionData={subscriptionData}
            />
          </div>
        )}
      </div>

      <div className="flex items-center space-x-4">
        {/* Notifications using Shadcn Popover */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="p-1 rounded-md hover:bg-accent focus:outline-none"
              aria-label="Notifications"
            >
              <NotificationIcon hasNotifications={unreadCount > 0} />
            </Button>
          </PopoverTrigger>
          <NotificationsPopover
            notifications={notifications}
            onMarkAsRead={markNotificationAsRead}
            onMarkAllAsRead={markAllNotificationsAsRead}
            isLoading={isLoadingNotifications}
            onLoadMore={loadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
          />
        </Popover>

        {/* User profile using Shadcn DropdownMenu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center space-x-2 focus:outline-none p-1"
              aria-label="User profile"
            >
              <Avatar className="h-8 w-8">
                {currentUser?.imageUrl && <AvatarImage src={currentUser.imageUrl} alt="Profile" />}
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                  {currentUser.firstName?.[0]}
                  {currentUser.lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{currentUser.firstName}</span>
              <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Button>
          </DropdownMenuTrigger>
          <ProfileDropdown currentUser={currentUser} onLogout={onLogout} />
        </DropdownMenu>
      </div>
    </header>
  );
}
